import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  FaSignInAlt, FaSignOutAlt, FaBars, FaTimes, FaApple, FaGooglePlay,
  FaInfoCircle, FaEnvelope as FaEnvelopeI, FaHandshake, FaQuestionCircle,
  FaLock, FaFileContract, FaUndo as FaUndoI, FaBuilding,
  FaHome, FaDrumstickBite as FaDrumstickBiteI, FaUsers, FaChartLine as FaChartLineI,
  FaTrashAlt as FaTrashAltI,
} from 'react-icons/fa';
import type { ComponentType } from 'react';
import { FunctionComponent } from 'react';
import Home from './components/Home';
import SignUp from './components/SignUp';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import DeleteProfile from './components/DeleteProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import RefundPolicy from './components/RefundPolicy';
import Profile from './components/Profile';
import AboutUs from './components/AboutUs';
import CompanyDetails from './components/CompanyDetails';
import SocialLoginCallback from './components/SocialLoginCallback';
import { logoutUser, fetchUserProfile } from './lib/api';
import { isInfluencerLoggedIn as checkInfluencerAuth } from './lib/influencer-api';
import Contact from './components/Contact';
import logo from './assets/logo.png';
import EmailConfirmation from './components/EmailConfirmation';
import Features from './components/Features';
import InfluencerLogin from './components/InfluencerLogin';
import InfluencerDashboard from './components/InfluencerDashboard';
import InfluencerLanding from './components/InfluencerLanding';
import InfluencerForgotPassword from './components/InfluencerForgotPassword';
import InfluencerResetPassword from './components/InfluencerResetPassword';
import AdminPortalLogin from './components/AdminPortalLogin';
import AdminPortalDashboard from './components/AdminPortalDashboard';
import AdminPortalUserLookup from './components/AdminPortalUserLookup';
import AdminPortalUserDetail from './components/AdminPortalUserDetail';
import AdminPortalCommunity from './components/AdminPortalCommunity';
import AdminPortalReferrals from './components/AdminPortalReferrals';
import AdminPortalInfluencerDetail from './components/AdminPortalInfluencerDetail';
import AdminPortalSubscriptions from './components/AdminPortalSubscriptions';
import AdminPortalSettings from './components/AdminPortalSettings';
import AdminPortalRecipes from './components/AdminPortalRecipes';
import AdminPortalPlans from './components/AdminPortalPlans';
import AdminPortalBulkEmail from './components/AdminPortalBulkEmail';
import AdminPortalAnalytics from './components/AdminPortalAnalytics';
import { AdminMe, adminPortalMe, getCachedMe, isAdminPortalLoggedIn, logoutAdminPortal } from './lib/admin-portal-api';
import Community from './components/Community';
import CommunityModeration from './components/CommunityModeration';
import Tracker from './components/Tracker';
import Recipes from './components/Recipes';
import FAQ from './components/FAQ';
import RecipePreviewPage from './components/RecipePreviewPage';
import FaqPreviewPage from './components/FaqPreviewPage';
import MealPlanPreviewPage from './components/MealPlanPreviewPage';

// react-icons v5 + React 19 type collision shim
const Ic = (C: any): ComponentType<{ className?: string }> => C as ComponentType<{ className?: string }>;
const InfoI = Ic(FaInfoCircle);
const MailI = Ic(FaEnvelopeI);
const HandshakeI = Ic(FaHandshake);
const FAQI = Ic(FaQuestionCircle);
const LockI = Ic(FaLock);
const ContractI = Ic(FaFileContract);
const RefundI = Ic(FaUndoI);
const BuildingI = Ic(FaBuilding);
const TrashI = Ic(FaTrashAltI);
const HomeI = Ic(FaHome);
const DrumstickI = Ic(FaDrumstickBiteI);
const UsersI = Ic(FaUsers);
const ChartLineI = Ic(FaChartLineI);

/** Wrapper that loads admin "me" or bounces to /admin-portal/login. */
function AdminPortalGate({ children }: { children: (me: AdminMe) => React.ReactElement }) {
  const [me, setMe] = useState<AdminMe | null>(() => getCachedMe());
  const [checking, setChecking] = useState<boolean>(!getCachedMe());
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!isAdminPortalLoggedIn()) { setDenied(true); setChecking(false); return; }
    // Re-validate against the server in case token expired
    adminPortalMe()
      .then((fresh) => { setMe(fresh); localStorage.setItem('admin_portal_me', JSON.stringify(fresh)); })
      .catch(() => { logoutAdminPortal(); setDenied(true); })
      .finally(() => setChecking(false));
  }, []);

  if (denied) return <Navigate to="/admin-portal/login" replace />;
  if (checking || !me) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">Loading…</div>;
  return children(me);
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('jwt'));
  const [isInfluencerLoggedIn, setIsInfluencerLoggedIn] = useState(checkInfluencerAuth());
  const [isPremium, setIsPremium] = useState<boolean>(() => localStorage.getItem('is_premium') === '1');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = () => {
      const jwt = localStorage.getItem('jwt');
      setIsLoggedIn(!!jwt);
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Refresh is_premium whenever the user is logged in. Cached in localStorage
  // so the nav doesn't flash the wrong state on reload.
  useEffect(() => {
    if (!isLoggedIn) {
      setIsPremium(false);
      localStorage.removeItem('is_premium');
      return;
    }
    const token = localStorage.getItem('jwt');
    if (!token) return;
    fetchUserProfile(token)
      .then((u: any) => {
        const premium = u?.is_premium === true;
        setIsPremium(premium);
        localStorage.setItem('is_premium', premium ? '1' : '0');
      })
      .catch(() => { /* leave cached value */ });
  }, [isLoggedIn]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  // Scroll to top whenever the route changes, unless we navigated to an in-page anchor.
  // Admin portal manages its own scroll.
  useEffect(() => {
    if (location.hash) return;
    if (location.pathname.startsWith('/admin-portal')) return;
    if (location.pathname.startsWith('/recipe-preview')) return;
    if (location.pathname.startsWith('/faq-preview')) return;
    if (location.pathname.startsWith('/meal-plan-preview')) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const handleLogout = () => {
    logoutUser();
    setIsLoggedIn(false);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => setIsMenuOpen((o) => !o);

  const FaSignInAltIcon = FaSignInAlt as FunctionComponent<{ className?: string }>;
  const FaSignOutAltIcon = FaSignOutAlt as FunctionComponent<{ className?: string }>;
  const FaBarsIcon = FaBars as FunctionComponent<{ className?: string }>;
  const FaTimesIcon = FaTimes as FunctionComponent<{ className?: string }>;
  const FaAppleIcon = FaApple as FunctionComponent<{ className?: string }>;
  const FaGooglePlayIcon = FaGooglePlay as FunctionComponent<{ className?: string }>;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
      isActive ? 'text-primary bg-primary/10' : 'text-gray-700 hover:text-primary hover:bg-gray-50'
    }`;

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
      isActive ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
    }`;

  const inAdminPortal = location.pathname.startsWith('/admin-portal');
  const inRecipePreview = location.pathname.startsWith('/recipe-preview');
  const inFaqPreview = location.pathname.startsWith('/faq-preview');
  const inMealPlanPreview = location.pathname.startsWith('/meal-plan-preview');

  // Strapi admin iframe previews — render only the page, no site chrome.
  if (inRecipePreview || inFaqPreview || inMealPlanPreview) {
    return (
      <Routes>
        <Route path="/recipe-preview/:documentId" element={<RecipePreviewPage />} />
        <Route path="/faq-preview/:documentId" element={<FaqPreviewPage />} />
        <Route path="/meal-plan-preview/:documentId" element={<MealPlanPreviewPage />} />
      </Routes>
    );
  }

  // Admin Portal renders its own chrome — bypass the regular site nav + footer.
  if (inAdminPortal) {
    return (
      <Routes>
        <Route path="/admin-portal/login" element={<AdminPortalLogin onLoggedIn={() => { window.dispatchEvent(new Event('storage')); }} />} />
        <Route path="/admin-portal" element={<AdminPortalGate>{(me) => <AdminPortalDashboard me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/users" element={<AdminPortalGate>{(me) => <AdminPortalUserLookup me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/users/:id" element={<AdminPortalGate>{(me) => <AdminPortalUserDetail me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/community" element={<AdminPortalGate>{(me) => <AdminPortalCommunity me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/referrals" element={<AdminPortalGate>{(me) => <AdminPortalReferrals me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/referrals/:id" element={<AdminPortalGate>{(me) => <AdminPortalInfluencerDetail me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/subscriptions" element={<AdminPortalGate>{(me) => <AdminPortalSubscriptions me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/recipes" element={<AdminPortalGate>{(me) => <AdminPortalRecipes me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/settings" element={<AdminPortalGate>{(me) => <AdminPortalSettings me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/plans" element={<AdminPortalGate>{(me) => <AdminPortalPlans me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/bulk-email" element={<AdminPortalGate>{(me) => <AdminPortalBulkEmail me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/analytics" element={<AdminPortalGate>{(me) => <AdminPortalAnalytics me={me} />}</AdminPortalGate>} />
        <Route path="*" element={<Navigate to="/admin-portal" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className={`sticky top-0 z-50 transition-all duration-200 border-b border-gray-200 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-white'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 md:px-6">
          <div className={`flex items-center justify-between transition-all duration-200 ${isScrolled ? 'h-14' : 'h-16'}`}>
            <NavLink to="/" aria-label="EatMeat Home" className="flex items-center gap-2.5 group flex-shrink-0">
              <img src={logo} alt="EatMeat Logo" className="w-10 h-10 object-contain group-hover:scale-110 transition-transform" />
              <span className="font-extrabold text-lg text-gray-900 tracking-tight">EatMeat</span>
            </NavLink>

            <ul className="hidden md:flex items-center gap-1">
              {isLoggedIn ? (
                <>
                  <li><NavLink to="/profile" className={navLinkClass} end>Dashboard</NavLink></li>
                  {isPremium && <li><NavLink to="/recipes" className={navLinkClass}>Recipes</NavLink></li>}
                  <li><NavLink to="/tracker" className={navLinkClass}>Tracker</NavLink></li>
                  <li><NavLink to="/community" className={navLinkClass}>Community</NavLink></li>
                </>
              ) : (
                <>
                  <li><NavLink to="/" className={navLinkClass} end>Home</NavLink></li>
                  <li><NavLink to="/recipes" className={navLinkClass}>Recipes</NavLink></li>
                  <li><NavLink to="/community" className={navLinkClass}>Community</NavLink></li>
                  <li><NavLink to="/tracker" className={navLinkClass}>Tracker</NavLink></li>
                  <li className="relative group">
                    <button className="relative px-3 py-1.5 rounded-full text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gray-50 transition-all flex items-center gap-1">
                      More
                      <svg className="w-3 h-3 transition-transform group-hover:rotate-180" viewBox="0 0 12 12" fill="none"><path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <div className="absolute top-full right-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div className="w-56 bg-white rounded-2xl shadow-xl border border-gray-100 p-1.5">
                        {[
                          { to: '/about', label: 'About', IconC: InfoI },
                          { to: '/contact', label: 'Contact', IconC: MailI },
                          { to: '/influencers', label: 'Influencer Program', IconC: HandshakeI },
                          { to: '/faq', label: 'FAQ', IconC: FAQI },
                        ].map((item) => {
                          const I = item.IconC;
                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  isActive ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                                }`
                              }
                            >
                              <I className="text-gray-500" />
                              {item.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  </li>
                  <li className="relative group">
                    <button className="relative px-3 py-1.5 rounded-full text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gray-50 transition-all flex items-center gap-1">
                      Legal
                      <svg className="w-3 h-3 transition-transform group-hover:rotate-180" viewBox="0 0 12 12" fill="none"><path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <div className="absolute top-full right-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div className="w-52 bg-white rounded-2xl shadow-xl border border-gray-100 p-1.5">
                        {[
                          { to: '/privacy-policy', label: 'Privacy Policy', IconC: LockI },
                          { to: '/terms', label: 'Terms & Conditions', IconC: ContractI },
                          { to: '/refund-policy', label: 'Refund Policy', IconC: RefundI },
                          { to: '/company-details', label: 'Company Details', IconC: BuildingI },
                        ].map((item) => {
                          const I = item.IconC;
                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  isActive ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                                }`
                              }
                            >
                              <I className="text-gray-500" />
                              {item.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  </li>
                </>
              )}
            </ul>

            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              {isLoggedIn ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Log Out"
                >
                  <FaSignOutAltIcon className="h-4 w-4" /> Log Out
                </button>
              ) : (
                <>
                  <NavLink to="/login" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors">
                    <FaSignInAltIcon className="h-4 w-4" /> Log In
                  </NavLink>
                  <NavLink to="/signup" className="px-4 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r from-primary to-orange-500 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all">
                    Sign Up Free
                  </NavLink>
                </>
              )}
            </div>

            <button
              className="md:hidden w-10 h-10 rounded-xl flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <FaTimesIcon className="h-5 w-5" /> : <FaBarsIcon className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        <div
          className={`md:hidden fixed top-0 right-0 w-80 max-w-[85vw] h-full bg-white shadow-2xl transform transition-transform duration-300 ease-out z-50 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <img src={logo} alt="EatMeat" className="w-9 h-9 object-contain" />
              <span className="font-extrabold text-base text-gray-900">EatMeat</span>
            </div>
            <button
              onClick={toggleMenu}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
              aria-label="Close menu"
            >
              <FaTimesIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="overflow-y-auto h-[calc(100vh-64px)] p-3">
            {isLoggedIn ? (
              <>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Navigation</div>
                <ul className="space-y-1 mb-4">
                  <li><NavLink to="/profile" className={mobileLinkClass} onClick={toggleMenu} end><HomeI className="text-gray-500" /> Dashboard</NavLink></li>
                  {isPremium && (
                    <li><NavLink to="/recipes" className={mobileLinkClass} onClick={toggleMenu}><DrumstickI className="text-gray-500" /> Recipes</NavLink></li>
                  )}
                  <li><NavLink to="/tracker" className={mobileLinkClass} onClick={toggleMenu}><ChartLineI className="text-gray-500" /> Tracker</NavLink></li>
                  <li><NavLink to="/community" className={mobileLinkClass} onClick={toggleMenu}><UsersI className="text-gray-500" /> Community</NavLink></li>
                </ul>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Account</div>
                <ul className="space-y-1">
                  <li><NavLink to="/delete-profile" className={mobileLinkClass} onClick={toggleMenu}><TrashI className="text-gray-500" /> Delete Account</NavLink></li>
                  <li>
                    <button
                      onClick={() => { handleLogout(); toggleMenu(); }}
                      className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 w-full transition-colors"
                    >
                      <FaSignOutAltIcon className="h-4 w-4" /> Log Out
                    </button>
                  </li>
                </ul>
              </>
            ) : (
              <>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Menu</div>
                <ul className="space-y-1 mb-4">
                  <li><NavLink to="/" className={mobileLinkClass} onClick={toggleMenu} end><HomeI className="text-gray-500" /> Home</NavLink></li>
                  <li><NavLink to="/recipes" className={mobileLinkClass} onClick={toggleMenu}><DrumstickI className="text-gray-500" /> Recipes</NavLink></li>
                  <li><NavLink to="/community" className={mobileLinkClass} onClick={toggleMenu}><UsersI className="text-gray-500" /> Community</NavLink></li>
                  <li><NavLink to="/tracker" className={mobileLinkClass} onClick={toggleMenu}><ChartLineI className="text-gray-500" /> Tracker</NavLink></li>
                </ul>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">More</div>
                <ul className="space-y-1 mb-4">
                  <li><NavLink to="/about" className={mobileLinkClass} onClick={toggleMenu}><InfoI className="text-gray-500" /> About</NavLink></li>
                  <li><NavLink to="/contact" className={mobileLinkClass} onClick={toggleMenu}><MailI className="text-gray-500" /> Contact</NavLink></li>
                  <li><NavLink to="/influencers" className={mobileLinkClass} onClick={toggleMenu}><HandshakeI className="text-gray-500" /> Influencer Program</NavLink></li>
                  <li><NavLink to="/faq" className={mobileLinkClass} onClick={toggleMenu}><FAQI className="text-gray-500" /> FAQ</NavLink></li>
                </ul>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Legal</div>
                <ul className="space-y-1 mb-4">
                  <li><NavLink to="/privacy-policy" className={mobileLinkClass} onClick={toggleMenu}><LockI className="text-gray-500" /> Privacy Policy</NavLink></li>
                  <li><NavLink to="/terms" className={mobileLinkClass} onClick={toggleMenu}><ContractI className="text-gray-500" /> Terms</NavLink></li>
                  <li><NavLink to="/refund-policy" className={mobileLinkClass} onClick={toggleMenu}><RefundI className="text-gray-500" /> Refund</NavLink></li>
                </ul>

                <div className="px-3 pt-4 space-y-2 border-t border-gray-100">
                  <NavLink
                    to="/signup"
                    onClick={toggleMenu}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-orange-500 shadow-md shadow-primary/20"
                  >
                    Sign Up Free
                  </NavLink>
                  <NavLink
                    to="/login"
                    onClick={toggleMenu}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 hover:border-primary hover:text-primary transition-colors"
                  >
                    <FaSignInAltIcon className="h-4 w-4" /> Log In
                  </NavLink>
                </div>
              </>
            )}
          </div>
        </div>

        {isMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={toggleMenu}
            aria-hidden="true"
          />
        )}
      </header>

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={isLoggedIn ? <Navigate to="/profile" replace /> : <Home isLoggedIn={isLoggedIn} />} />
          <Route path="/about" element={isLoggedIn ? <Navigate to="/profile" replace /> : <AboutUs isLoggedIn={isLoggedIn} />} />
          <Route path="/company-details" element={<CompanyDetails />} />
          <Route path="/signup" element={isLoggedIn ? <Navigate to="/profile" replace /> : <SignUp setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/login" element={isLoggedIn ? <Navigate to="/profile" replace /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/features" element={isLoggedIn ? <Navigate to="/profile" replace /> : <Features isLoggedIn />} />
          <Route path="/email-confirmation" element={<EmailConfirmation isLoggedIn={isLoggedIn} />} />
          <Route
            path="/profile"
            element={
              isLoggedIn ? <Profile setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" replace state={{ from: location.pathname }} />
            }
          />
          <Route path="/delete-profile" element={<DeleteProfile setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/auth/:provider/callback" element={<SocialLoginCallback setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={isLoggedIn ? <Navigate to="/profile" replace /> : <Contact isLoggedIn={isLoggedIn} />} />
          <Route path="/community" element={<Community isLoggedIn={isLoggedIn} />} />
          <Route path="/community/moderation" element={<CommunityModeration isLoggedIn={isLoggedIn} />} />
          <Route path="/tracker" element={<Tracker isLoggedIn={isLoggedIn} />} />
          <Route path="/recipes" element={<Recipes isLoggedIn={isLoggedIn} isPremium={isPremium} />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/influencers" element={isLoggedIn ? <Navigate to="/profile" replace /> : <InfluencerLanding isLoggedIn={isLoggedIn} />} />
          <Route path="/influencer" element={
            isInfluencerLoggedIn ? <Navigate to="/influencer/dashboard" replace /> : <InfluencerLogin setIsInfluencerLoggedIn={setIsInfluencerLoggedIn} />
          } />
          <Route path="/influencer/dashboard" element={
            isInfluencerLoggedIn ? <InfluencerDashboard setIsInfluencerLoggedIn={setIsInfluencerLoggedIn} /> : <Navigate to="/influencer" replace />
          } />
          <Route path="/influencer/forgot-password" element={<InfluencerForgotPassword />} />
          <Route path="/influencer/reset-password" element={<InfluencerResetPassword />} />

          {/* ── Admin Portal (Strapi-admin-SSO branded UI) ── */}
          <Route path="/admin-portal/login" element={<AdminPortalLogin onLoggedIn={() => { /* re-render via storage event */ window.dispatchEvent(new Event('storage')); }} />} />
          <Route path="/admin-portal" element={<AdminPortalGate>{(me) => <AdminPortalDashboard me={me} />}</AdminPortalGate>} />
          <Route path="/admin-portal/users" element={<AdminPortalGate>{(me) => <AdminPortalUserLookup me={me} />}</AdminPortalGate>} />
          <Route path="/admin-portal/users/:id" element={<AdminPortalGate>{(me) => <AdminPortalUserDetail me={me} />}</AdminPortalGate>} />
          <Route path="/admin-portal/community" element={<AdminPortalGate>{(me) => <AdminPortalCommunity me={me} />}</AdminPortalGate>} />
          <Route path="/admin-portal/referrals" element={<AdminPortalGate>{(me) => <AdminPortalReferrals me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/referrals/:id" element={<AdminPortalGate>{(me) => <AdminPortalInfluencerDetail me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/subscriptions" element={<AdminPortalGate>{(me) => <AdminPortalSubscriptions me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/recipes" element={<AdminPortalGate>{(me) => <AdminPortalRecipes me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/settings" element={<AdminPortalGate>{(me) => <AdminPortalSettings me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/plans" element={<AdminPortalGate>{(me) => <AdminPortalPlans me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/bulk-email" element={<AdminPortalGate>{(me) => <AdminPortalBulkEmail me={me} />}</AdminPortalGate>} />
        <Route path="/admin-portal/analytics" element={<AdminPortalGate>{(me) => <AdminPortalAnalytics me={me} />}</AdminPortalGate>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {!(isLoggedIn && ['/profile', '/tracker', '/community'].some((p) => location.pathname.startsWith(p))) && (
      <footer className="relative bg-gray-900 text-white overflow-hidden">
        {/* Ambient orange glow at top */}
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[40rem] h-40 bg-primary/20 blur-3xl rounded-full" />

        <div className="relative container mx-auto px-6 py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={logo} alt="EatMeat" className="w-10 h-10 object-contain" />
                <span className="font-extrabold text-xl tracking-tight">EatMeat</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Your ultimate carnivore & ketovore meal planning app. Recipes, meal plans, health tracking, and a thriving community.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <a href="https://apps.apple.com/app/eatmeat/id6746769046" target="_blank" rel="noopener noreferrer"
                  className="px-3.5 py-2 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 hover:border-white/20 hover:-translate-y-0.5 transition-all flex items-center gap-2">
                  <FaAppleIcon className="h-4 w-4" /> App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat" target="_blank" rel="noopener noreferrer"
                  className="px-3.5 py-2 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 hover:border-white/20 hover:-translate-y-0.5 transition-all flex items-center gap-2">
                  <FaGooglePlayIcon className="h-4 w-4" /> Google Play
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-xs uppercase tracking-widest text-white">Features</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><NavLink to="/community" className="hover:text-primary transition-colors">Community</NavLink></li>
                <li><NavLink to="/tracker" className="hover:text-primary transition-colors">Health Tracker</NavLink></li>
                <li><NavLink to="/recipes" className="hover:text-primary transition-colors">Recipes</NavLink></li>
                <li><NavLink to="/about" className="hover:text-primary transition-colors">About Us</NavLink></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-xs uppercase tracking-widest text-white">Legal</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><NavLink to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</NavLink></li>
                <li><NavLink to="/terms" className="hover:text-primary transition-colors">Terms & Conditions</NavLink></li>
                <li><NavLink to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</NavLink></li>
                <li><NavLink to="/company-details" className="hover:text-primary transition-colors">Company Details</NavLink></li>
                <li><NavLink to="/faq" className="hover:text-primary transition-colors">FAQ</NavLink></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-xs uppercase tracking-widest text-white">Contact</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><a href="mailto:support@eatmeatapp.com" className="hover:text-primary transition-colors break-all">support@eatmeatapp.com</a></li>
                <li><NavLink to="/contact" className="hover:text-primary transition-colors">Contact Form</NavLink></li>
                <li><NavLink to="/influencers" className="hover:text-primary transition-colors">Influencer Program</NavLink></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">&copy; {new Date().getFullYear()} EatMeat. All rights reserved.</p>
            <div className="flex gap-5 text-xs text-gray-500">
              <NavLink to="/privacy-policy" className="hover:text-white transition-colors">Privacy</NavLink>
              <NavLink to="/terms" className="hover:text-white transition-colors">Terms</NavLink>
              <a href="mailto:support@eatmeatapp.com" className="hover:text-white transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;
